import { gql } from '@apollo/client';
import { endOfToday, sub } from 'date-fns';
import { useAtomValue } from 'jotai';
import { ComponentProps, useMemo } from 'react';

import { type PreVisitSummaryCardConnected } from '@grow-therapy-team/seedling-components';

import { Server } from '../../../apollo/types';
import { useAppDependenciesContext } from '../../../state';
import { GET_IN_SESSION_APPOINTMENT } from '../../schedule-appointment/useInSessionAppointment';
import { scheduledPatientInformationAtom } from '../../schedule-preview/state';
import { AppointmentType } from '../../schedule-preview/types';
import { isInSessionWithSelectedClientAtom, providerShortIdAtom, selectedClientInfoAtom } from '../../state';
import { GET_PATIENT_BY_PATIENT_SHORT_ID_GQL, GetPatientByIdQueryData } from '../../useGetPatientByShortId';
import { resolveAppointmentType } from '../utils';
import { PreVisitSummaryCardNudgeWrapper } from './PreVisitSummaryCardNudgeWrapper';
import {
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
  PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables,
} from './types';
import { useResourceButtonOnClickHandlers } from './useResourceButtonOnClickHandlers';

const TIME_START_LOWER_BOUND_MINUTES = 15;

type ViewModel = Partial<
  Pick<
    ComponentProps<typeof PreVisitSummaryCardConnected>,
    'appointmentShortId' | 'patientShortId' | 'nudgeContext' | 'preVisitSummaryCardComponent'
  >
> &
  Pick<ComponentProps<typeof PreVisitSummaryCardConnected>, 'resourceButtonOnClickHandlers'> & {
    appointmentType?: AppointmentType | string;
  };

/**
 * Intended to query for the selected patient's upcoming appointment's short ID.
 */
export const PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY = gql`
  query PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatient($filters: AppointmentsFilter!) {
    allAppointments(filters: $filters) {
      edges {
        node {
          id
          appointmentShortId
          appointmentType
        }
      }
    }
  }
`;

type AppointmentTypeSource = {
  appointmentShortId?: string | null;
  appointmentType?: string | null;
};

type InSessionAppointmentsQueryData = {
  allAppointments?: {
    edges?: Array<{ node?: AppointmentTypeSource | null } | null> | null;
  } | null;
};

function getFifteenMinutesAgoISO() {
  return sub(new Date(), {
    minutes: TIME_START_LOWER_BOUND_MINUTES,
  }).toISOString();
}

function appointmentTypeForShortId(
  edges: NonNullable<InSessionAppointmentsQueryData['allAppointments']>['edges'],
  appointmentShortId: string,
) {
  const matchingAppointment = edges?.find((edge) => edge?.node?.appointmentShortId === appointmentShortId)?.node;

  return matchingAppointment?.appointmentType || undefined;
}

export function usePreVisitSummaryCardWrapper(): ViewModel {
  const isInSessionWithSelectedClient = useAtomValue(isInSessionWithSelectedClientAtom);
  const selectedPatientInfo = useAtomValue(selectedClientInfoAtom);
  const scheduledPatientInfo = useAtomValue(scheduledPatientInformationAtom);
  const patientShortId = selectedPatientInfo?.patientShortId || scheduledPatientInfo?.patientShortId || '';
  // Schedule preview can still describe another client after this one is selected.
  const scheduledAppointmentShortId =
    selectedPatientInfo?.patientShortId && scheduledPatientInfo?.patientShortId !== selectedPatientInfo.patientShortId
      ? undefined
      : scheduledPatientInfo?.appointmentShortId;
  const selectedAppointmentShortId = selectedPatientInfo?.inSessionAppointmentShortId || scheduledAppointmentShortId;
  const providerShortId = useAtomValue(providerShortIdAtom);
  const { useQuery } = useAppDependenciesContext();

  const alreadyHasAppointmentShortId = !!selectedAppointmentShortId;

  const fifteenMinutesAgoISO = useMemo(getFifteenMinutesAgoISO, []);

  const { data } = useQuery<
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQuery,
    PreVisitSummaryCardWrapper__GetUpcomingAppointmentForPatientQueryVariables
  >(PRE_VISIT_SUMMARY_CARD_WRAPPER__GET_UPCOMING_APPOINTMENT_FOR_PATIENT_QUERY, {
    skip: !!alreadyHasAppointmentShortId || !patientShortId,
    variables: {
      filters: {
        patientShortIds: [patientShortId],
        timeStartRange: {
          begin: fifteenMinutesAgoISO,
          end: endOfToday().toISOString(),
        },
        canceled: false,
      },
    },
    context: { server: Server.MONOLITH },
  });

  const inSessionAppointmentShortId = isInSessionWithSelectedClient
    ? selectedPatientInfo?.inSessionAppointmentShortId
    : undefined;

  const { data: inSessionAppointmentsData } = useQuery<InSessionAppointmentsQueryData>(GET_IN_SESSION_APPOINTMENT, {
    skip: !inSessionAppointmentShortId || !patientShortId,
    variables: {
      filters: {
        patientShortIds: [patientShortId],
        canceled: false,
      },
    },
    context: { server: Server.MONOLITH },
  });

  const { data: patientChartData, loading: isPatientChartLoading } = useQuery<GetPatientByIdQueryData>(
    GET_PATIENT_BY_PATIENT_SHORT_ID_GQL,
    {
      skip: !patientShortId,
      variables: { patientShortId },
      context: { server: Server.MONOLITH },
    },
  );

  const upcomingAppointment = data?.allAppointments?.edges?.[0]?.node;
  const upcomingAppointmentShortId = upcomingAppointment?.appointmentShortId;
  const inSessionAppointmentType = inSessionAppointmentShortId
    ? appointmentTypeForShortId(inSessionAppointmentsData?.allAppointments?.edges, inSessionAppointmentShortId)
    : undefined;
  const lastAppointment = patientChartData?.patientChart?.lastAppointment;

  const appointmentShortId =
    selectedAppointmentShortId || upcomingAppointmentShortId || lastAppointment?.appointmentShortId || undefined;
  const appointmentType = resolveAppointmentType({
    patientShortId,
    candidates: [
      {
        patientShortId: inSessionAppointmentShortId ? patientShortId : undefined,
        appointmentType: inSessionAppointmentType,
      },
      {
        patientShortId: scheduledPatientInfo?.patientShortId,
        appointmentType: scheduledPatientInfo?.appointmentType,
      },
      {
        patientShortId: upcomingAppointment ? patientShortId : undefined,
        appointmentType: upcomingAppointment?.appointmentType,
      },
    ],
    lastAppointment,
    isLastAppointmentLoading: isPatientChartLoading,
  });

  const resourceButtonOnClickHandlers = useResourceButtonOnClickHandlers({
    appointmentShortId,
    patientShortId,
    providerShortId,
  });

  return {
    appointmentShortId,
    patientShortId,
    appointmentType,
    nudgeContext: isInSessionWithSelectedClient ? 'telehealth-session' : 'telehealth-waiting-room',
    preVisitSummaryCardComponent: PreVisitSummaryCardNudgeWrapper,
    resourceButtonOnClickHandlers,
  };
}
